import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, push, onValue } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

const CATEGORIES = [
  { id: 'ride', label: 'Ride Issue', icon: '🚗' },
  { id: 'payment', label: 'Payment', icon: '💰' },
  { id: 'driver', label: 'Driver Conduct', icon: '👤' },
  { id: 'app', label: 'App Bug', icon: '🐛' },
  { id: 'other', label: 'Other', icon: '📝' },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [category, setCategory] = useState('ride');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  useEffect(() => {
    if (!user || !user.phone) return;
    const feedbackRef = ref(db, 'feedback/');
    const unsubscribe = onValue(feedbackRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(f => f.fromPhone === user.phone)
          .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        setHistory(list);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const submitFeedback = async () => {
    if (!message.trim() || message.trim().length < 10) {
      alert('Please describe your issue in at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const catLabel = CATEGORIES.find(c => c.id === category)?.label || 'Other';
      await push(ref(db, 'feedback/'), {
        category: catLabel,
        message: message.trim(),
        fromName: user.name,
        fromPhone: user.phone,
        status: 'open',
        createdAt: new Date().toISOString(),
      });
      setSubmitting(false);
      setMessage('');
      alert("We've received your feedback and will get back to you soon.");
    } catch (error) {
      setSubmitting(false);
      alert('Error: ' + error.message);
    }
  };

  if (!user) return null;

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>💬 {t('feedback')} & Grievance</h2>

        <div style={styles.card}>
          <h3 style={{ margin: '0 0 12px', color: '#1a1a2e' }}>What's this about?</h3>
          <div style={styles.categoryGrid}>
            {CATEGORIES.map(cat => (
              <button key={cat.id} style={category === cat.id ? styles.catChipActive : styles.catChip} onClick={() => setCategory(cat.id)}>
                <div style={{ fontSize: 18 }}>{cat.icon}</div>
                <div style={{ fontSize: 10, fontWeight: 600 }}>{cat.label}</div>
              </button>
            ))}
          </div>

          <label style={styles.fieldLabel}>Describe your issue</label>
          <textarea style={styles.textArea} placeholder="Tell us what happened..." value={message} onChange={e => setMessage(e.target.value)} rows={5} />

          <button style={styles.submitBtn} onClick={submitFeedback} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit feedback'}
          </button>
        </div>

        <h3 style={{ marginBottom: 10, color: '#1a1a2e' }}>Your submissions</h3>
        {history.length === 0 ? (
          <p style={{ color: '#9499b5', textAlign: 'center' }}>No submissions yet</p>
        ) : (
          history.map(f => (
            <div key={f.id} style={styles.historyCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <b style={{ fontSize: 13, color: '#1a1a2e' }}>{f.category}</b>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: f.status === 'resolved' ? '#eaf4ea' : '#fdf3e3', color: f.status === 'resolved' ? '#166534' : '#8a6416' }}>
                  {f.status === 'resolved' ? '✓ Resolved' : '⏳ Open'}
                </span>
              </div>
             <p style={{ fontSize: 12, color: '#5a5f7d', margin: '0 0 6px' }}>{f.message}</p>
<p style={{ fontSize: 10, color: '#9499b5', margin: 0 }}>{f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '—'}</p>

{f.adminResponse && (
  <div style={{ background: '#eaf4ea', borderRadius: 8, padding: 10, marginTop: 8 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', marginBottom: 2 }}>💬 Response from Guningo support:</div>
    <div style={{ fontSize: 12, color: '#166534' }}>{f.adminResponse}</div>
  </div>
)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 520, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', marginBottom: 20 },
  card: { border: '1px solid #eef1f8', borderRadius: 14, padding: 20, marginBottom: 20 },
  categoryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 8, marginBottom: 14 },
  catChip: { border: '1px solid #d9edfa', background: '#f7f9fc', borderRadius: 10, padding: 10, cursor: 'pointer' },
  catChipActive: { border: '1px solid #3a8fd0', background: '#e6f1fb', borderRadius: 10, padding: 10, cursor: 'pointer' },
  fieldLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#5a5f7d', marginBottom: 6 },
  textArea: { width: '100%', borderRadius: 10, border: '1px solid #d9edfa', padding: 12, fontSize: 14, boxSizing: 'border-box', marginBottom: 14, fontFamily: 'inherit', resize: 'vertical' },
  submitBtn: { width: '100%', height: 46, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  historyCard: { border: '1px solid #eef1f8', borderRadius: 12, padding: 12, marginBottom: 10 },
};